import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {ContentResponse} from "../interface/contentResponse";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {UserCardContentRequest} from "../interface/userCardContentRequest";
import {Action} from "../interface/action";
import {ContentType} from "../interface/contentType";
import {UserCardUtil} from "../utilityClass/userCardUtil";
import {ContentService} from "../service/content.service";
import {CreateCardComponent} from "../create-card/create-card.component";
import {EditProfileCardComponent} from "../profile-card/edit-profile-card/edit-profile-card.component";
import {ContentUtility} from "../utilityClass/contentUtilty";
import {SharedConstants} from "../utilityClass/sharedConstants";

@Component({
  selector: 'app-add-user-card-content',
  templateUrl: './add-user-card-content.component.html',
  styleUrl: './add-user-card-content.component.scss'
})
export class AddUserCardContentComponent implements OnInit, OnDestroy {

  contentResponse: ContentResponse | any
  form: FormGroup | any;
  selectedFile: File | any
  showError: boolean = false
  fileSizeExceeded: boolean = false
  contentIcon: string | any
  hasDocumentURL: boolean = false
  userCardId: bigint

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private userCardUtil: UserCardUtil,
    private dialogRef: MatDialogRef<AddUserCardContentComponent>,
    private contentService: ContentService,
    private contentUtility: ContentUtility,
  ) {
    this.userCardId = this.data.userCardId;
    this.contentResponse = this.data.content;
    this.contentIcon = this.contentService.getImage(this.contentResponse.contentIcon)
  }

  ngOnInit(): void {
    this.initializeForm();
    this.addValidationIfPaymentIsTelCell();
    if(this.isDocument()) {
      this.initHasDocumentURL();
    }
  }

  ngOnDestroy() {
    CreateCardComponent.contentAddEmitter.next(null);
    EditProfileCardComponent.contentEditEmitter.next(null);
  }

  initializeForm() {
    this.form = new FormGroup({
        placeholder: new FormControl('', [Validators.required, this.userCardUtil.whitespaceValidator()]),
        linkTitle: new FormControl('', [this.userCardUtil.whitespaceValidator(), Validators.maxLength(10)])},
      {
        updateOn: 'blur'
      }
    )
  }

  get documentName() {
    const filename = this.selectedFile?.name;
    return filename ? filename : 'No file chosen';
  }

  get createUserCardContentControl(): any {
    return this.form.controls;
  }

  isContact(): boolean {
    return this.contentResponse.contentType === ContentType.CONTACT;
  }

  isSocialMedia(): boolean {
    return this.contentResponse.contentType === ContentType.SOCIAL_MEDIA || this.contentResponse.contentType === ContentType.PAYMENT;
  }

  isDocument(): boolean {
    return this.contentResponse.contentType === ContentType.DOCUMENT
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  onEnter(event: any) {
    this.userCardUtil.onEnter(event)
  }

  saveUserCardContent(): void {
    this.form.markAllAsTouched()
    if(this.isDocument() && this.isBothDocumentFieldsInvalid()) {
      this.showError = true
    }
    if (!this.showError && !this.fileSizeExceeded) {
      const userCardContentRequest: UserCardContentRequest = {
        id: null,
        placeholder: this.getPlaceholder(),
        linkTitle: this.form.value.linkTitle ? this.form.value.linkTitle : '',
        contentName: this.contentResponse.name,
        type: this.contentResponse.contentType,
        contentId: this.contentResponse.id,
        contentIcon: this.contentResponse.contentIcon,
        active: true,
        action: Action.CREATED,
        singleLink: this.contentResponse.singleLink,
        userCardId: this.userCardId,
        identifier: this.contentUtility.generateRandomContentIdentifier()
      }

      const combinedData = {
        userCardContent: userCardContentRequest,
        documentFile: this.selectedFile,
      }

      CreateCardComponent.contentAddEmitter.next(combinedData);
      EditProfileCardComponent.contentEditEmitter.next(combinedData);
      this.dialogRef.close(true);
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const fileSizeInMB = file.size / (1024 * 1024);
      const maxSizeInMB = 5;
      this.showError = false
      this.fileSizeExceeded = false;
      this.selectedFile = file;
      this.form.get('placeholder')?.setValue('');
      this.form.get('placeholder')?.disable();

      if (fileSizeInMB > maxSizeInMB) {
        this.fileSizeExceeded = true;
        return;
      }
    }
  }

  protected initHasDocumentURL(): void {
    this.form.get('placeholder')?.valueChanges.subscribe({
      next: () => {
        const value = this.form.get('placeholder')?.value;
        this.showError = false;
        this.hasDocumentURL = (value && value.length > 0);
      }
    })
  }

  protected clickChooseFile(inputField: any): void {
    if (this.hasDocumentURL) return;
    inputField.click();
  }

  protected clearDocument() {
    this.selectedFile = null;
    this.clearDocumentErrors();
    this.form.get('placeholder')?.enable();
  }

  protected isBothDocumentFieldsInvalid() {
    const placeholder = this.form.get('placeholder')?.value;
    return (!this.selectedFile && !placeholder) || (!this.selectedFile && placeholder.length == 0)
  }

  private getPlaceholder() {
    const placeholder = this.form.value.placeholder;

    if(!this.isDocument()) {
      return placeholder;
    } else {
      if(placeholder && placeholder.length > 0) {
        return placeholder;
      } else {
        return this.selectedFile.name;
      }
    }
  }

  isShowPhoneNumber() {
    if (this.contentResponse.name === SharedConstants.VIBER ||
      this.contentResponse.name === SharedConstants.WHATS_APP ||
      this.contentResponse.name === SharedConstants.MESSAGE) {
      return true
    }
    return false;
  }

  addValidationIfPaymentIsTelCell(){
    if (this.contentResponse.contentType === ContentType.PAYMENT &&
      this.contentResponse.name.toLowerCase() === 'telcell') {
      this.form.get('placeholder').addValidators([Validators.pattern(/^\d{8}$/)]);

      this.form.get('placeholder').updateValueAndValidity();
    }
  }

  private clearDocumentErrors() {
    this.fileSizeExceeded = false;
  }
}
